/**
 * What each feature is FOR, and what it touches.
 *
 * DECLARED. `features.test.ts` fails when a folder appears under
 * src/features without a line here, so a new feature cannot quietly join the
 * app without saying what it does and which tables it reads.
 *
 * `routes` and `entities` are checked against the real router and the real
 * schema by the same test — a renamed route or a dropped table breaks the
 * build rather than rotting on this page.
 */
import type { FeatureEntry } from './types.ts'

/** Folders under src/features that are not features. */
export const nonFeatureFolders = ['shared']

export const features: FeatureEntry[] = [
  {
    id: 'auth',
    label: 'Accounts',
    purpose:
      'Signing up, signing in, recovering a forgotten password, and creating an account from an invitation link.',
    routes: [
      '/sign-in',
      '/sign-up',
      '/forgot-password',
      '/reset-password',
      '/create-account',
      '/confirm-email',
    ],
    entities: [
      'user',
      'session',
      'account',
      'verification',
      'invitation',
      'invitation_link',
      'pending_signup',
    ],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'home',
    label: 'Home',
    purpose:
      'Today: the landing screen — five numbers, what needs attention (overdue invoices, jobs ready to bill, invoices to send, unread messages, notices off tomorrow), today’s and tomorrow’s jobs, and recent activity. Real records in production (an empty database shows zeros and empty lists); the sample on the sandbox and in local development (D-019).',
    routes: ['/'],
    entities: [],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'jobs',
    label: 'Jobs',
    purpose:
      "Every visit at a client location — who cleans it, its status, what it bills and whether the customer was notified — and each job page with its Activity log, Edit, Cancel, Delete and Message (recorded as would send until Go live). Majik's hero flow (docs/majik).",
    routes: ['/jobs', '/jobs/$jobId'],
    entities: ['jobs', 'job_cleaners', 'job_work_types', 'work_types', 'communications'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'clients',
    label: 'Clients',
    purpose:
      "Majik's clients, each with its locations as tabs: the list with what each owes, and a client's page — Overview (locations, recurring jobs, monthly billing, people, billing setup, Activity), Jobs, Invoices, Messages, and a tab per location with its weekly service and recurring jobs on/off.",
    routes: ['/clients', '/clients/$clientId'],
    entities: [
      'clients',
      'locations',
      'client_contacts',
      'billing_items',
      'contacts',
      'contact_methods',
    ],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'messages',
    label: 'Messages',
    purpose:
      'Every text and email with clients and cleaners as one conversation per person: the inbox (All, Clients, Cleaners, search, unread marked), a conversation by day with Mark as read and a reply by text or email, New message, and Message all cleaners. Replies are recorded as would send until Go live.',
    routes: ['/messages'],
    entities: ['communications', 'communication_links', 'contacts', 'contact_methods'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'schedule',
    label: 'Schedule',
    purpose:
      'Two weeks at a time: every job, and the visits each recurring job will create (projected until its job is made, its lead days before); changing a visit for this visit only, this and following, or all; New recurring job and Edit; the list of recurring jobs.',
    routes: ['/schedule'],
    entities: ['recurring_jobs', 'recurring_job_cleaners', 'jobs'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'cleaners',
    label: 'Cleaners',
    purpose:
      "Majik's cleaners: the list by shift with each one's regular hours, where they work and their next job, and a cleaner's page — their regular week at each location with breaks counted (and Download schedule as a PDF), time off, coming up, details, Jobs, Messages, Edit, and a ⋯ with Send schedule (recorded as would send until Go live) and Time off (away means unavailable in the job picker and Today, and \"needs cover\" on the Schedule).",
    routes: ['/cleaners', '/cleaners/$cleanerId'],
    entities: ['cleaners', 'cleaner_time_off', 'contacts', 'contact_methods'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'inspections',
    label: 'Inspections',
    purpose:
      'Send the inspector one text listing the locations to check — who cleaned each and when — and see the inspections already sent. Send-only; recorded as would send until Go live.',
    routes: ['/inspections'],
    entities: ['inspections', 'inspection_locations'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'invoices',
    label: 'Invoices',
    purpose:
      "Monthly and one-off invoices: the list by what needs doing (needs action, waiting for payment, paid, void or written off) with what's owed and overdue, and an invoice's page with its lines, payment reminders, who it goes to and its Activity — New invoice, Edit lines, Send now, Send reminder, Edit status (Paid, Void, Uncollectible, back to waiting) and Delete for a draft. Sending to QuickBooks and emailing are recorded as would send until Go live.",
    routes: ['/invoices', '/invoices/$invoiceId'],
    entities: ['invoices', 'invoice_lines'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'orders',
    label: 'Orders',
    purpose:
      'Deliveries of supplies and equipment to a location — draft, ready, out for delivery, delivered — whose lines go on the client’s next invoice once delivered. New order, and an order’s page.',
    routes: ['/orders', '/orders/$orderId'],
    entities: ['orders', 'order_lines'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'inventory',
    label: 'Inventory',
    purpose:
      'The products Majik supplies, with their price and stock — never below 0, flagged at the low mark; counting stock never blocks an order. Add product, and edit one. Its own page since Oct 26 (it was a tab of Orders).',
    routes: ['/inventory'],
    entities: ['products'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'items',
    label: 'Items',
    purpose:
      'The example domain record. Replace it with the real one — every other feature reads it, so it is the template for how a record is defined, stored and scoped to an organisation.',
    routes: [],
    entities: ['items'],
    status: 'scaffold',
    origin: 'project',
  },
  {
    id: 'users',
    label: 'Users',
    purpose:
      'Everyone in the organization, people asking to join, and pending invitations — letting people in, inviting, role changes and removal — and Automatic access: the trusted email domains whose people join without waiting. Admin only, under Settings.',
    routes: ['/settings/users'],
    entities: ['member', 'invitation', 'user', 'auto_join_domain'],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    purpose:
      'Addresses other systems push live data to (ADR inbound-webhooks). Each webhook has a type and version, a private address shown once, an on/off switch, an optional expiry, and a run history of every delivery with what it added, updated and rejected — and any payload can be viewed with personal details masked. Data health: each webhook has a no-data alert (a rule in one sentence, autosaved) with a flag history; every Admin is emailed when it flags and when it clears, and sees a banner meanwhile; deliveries carry data checks; GET /api/health/feeds answers an outside monitor. Admin only, under Settings.',
    routes: ['/settings/webhooks'],
    entities: ['webhook_endpoint', 'webhook_delivery', 'webhook_alert_flag'],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'business',
    label: 'Business settings',
    purpose:
      'Settings → Business, Admins only: Go live (shadow mode, and the checklist that must be done first — the switch stays off until then), the business rules every screen reads, which team gets which alert and who is in each team, and the connections (none connected yet).',
    routes: ['/settings/business'],
    entities: ['business_rules', 'team', 'team_member', 'team_alert'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'monday-import',
    label: 'Import from Monday',
    purpose:
      'Settings → Import from Monday, Admins only: add the twelve Monday exports, see in plain counts what would be added, updated or left alone, then import — on the live site only.',
    routes: ['/settings/import'],
    entities: ['monday_import_run', 'monday_import_row', 'monday_record'],
    status: 'live',
    origin: 'project',
  },
  {
    id: 'settings',
    label: 'Settings',
    purpose:
      'How the workspace works: the admin-only home for Users, System and workspace preferences. Staff cannot open it.',
    routes: ['/settings'],
    entities: ['organization'],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'account',
    label: 'Account',
    purpose:
      'Your own profile and the light/dark choice. Personal, so every role has it — reached by clicking your name in the sidebar.',
    routes: ['/account'],
    entities: ['user'],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'impersonation',
    label: 'Impersonation',
    purpose:
      'An admin using the app exactly as a Staff member sees it, for up to an hour, under a red bar — every impersonation and every change made during it recorded on System. docs/IMPERSONATION.md.',
    routes: [],
    entities: ['impersonation_log', 'session'],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'assistant',
    label: 'Assistant',
    purpose:
      'The AI assistant, bottom-right on every screen: looks things up and proposes changes you approve — only ever what your role allows. docs/ASSISTANT.md.',
    routes: [],
    entities: ['assistant_conversation', 'assistant_message', 'app_setting'],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'mcp',
    label: 'MCP (AI apps)',
    purpose:
      'Claude, ChatGPT and other AI apps read the workspace through MCP at /mcp, signed in as a person — OAuth 2.1 with a consent screen, or a personal access token — and get exactly what that person can read, never a change. Account → Connected apps & tokens for your own; Settings → MCP access (Admin) for the on/off switch, every connection and the usage log. ADR mcp-server, docs/MCP.md.',
    routes: ['/settings/mcp', '/oauth/consent', '/oauth/error'],
    entities: [
      'mcp_token',
      'mcp_call',
      'oauth_client',
      'oauth_access_token',
      'oauth_refresh_token',
      'oauth_consent',
      'app_setting',
    ],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'search',
    label: 'Search',
    purpose:
      'Universal search in ⌘K and the header: records, people and pages, read live from the workspace and limited to what your role can open.',
    routes: [],
    entities: [
      'items',
      'member',
      'invitation',
      'clients',
      'locations',
      'contacts',
      'contact_methods',
      'cleaners',
      'jobs',
      'invoices',
      'orders',
    ],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'setup',
    label: 'Setup',
    purpose:
      'The first-run checklist: why a fresh deployment cannot take real users yet, and the steps to fix each thing. Public until the first account exists, because it is what explains why you cannot create one.',
    routes: ['/setup'],
    entities: [],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'environments',
    label: 'Environments',
    purpose:
      'Sandbox before live: the Sandbox strip on every page of a sandbox build, and the list of changes in it that production does not have yet — what is waiting for approval. docs/ENVIRONMENTS.md.',
    routes: [],
    entities: [],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'dictionary',
    label: 'Data dictionary',
    purpose:
      'What every field and code means, editable by Admins so every screen and AI app uses the same words — and record history, every change to every main record from every source, which the History section on a record shows (docs/DATA-MODEL.md).',
    routes: ['/settings/data-dictionary'],
    entities: ['data_field_override', 'data_code_override', 'record_history'],
    status: 'live',
    origin: 'template',
  },
  {
    id: 'system',
    label: 'System',
    purpose:
      'This dashboard. What the app is built from, what the tables look like, what the API exposes, and how much of it is being used.',
    routes: ['/settings/system'],
    entities: [],
    status: 'live',
    origin: 'template',
  },
]

export const featuresById = new Map(features.map((feature) => [feature.id, feature]))
